import {
  AttachmentSourceType,
  DMAutomationContent,
  DMAutomationContentButton,
  DMAutomationContentButtonType,
  MassTextAttachmentType,
  NotificationType,
} from "@markit/common.types";
import { HorizontalDivider } from "../../../Dividers/HorizontalDivider";
import { Colors } from "../../../../utils/colors";
import RectangleButton from "../../../Buttons/RectangleButton";
import { useCallback, useMemo, useState } from "react";
import { BackButton } from "../../../Buttons/BackButton";
import { showNotificationBanner } from "../../../../utils/notificationUtils";
import { useDispatch, useSelector } from "react-redux";
import { API } from "../../../../API";
import { Icon } from "@iconify/react";
import {
  defaultProductListView,
  detectedCensored,
  dmMessageContainsLink,
  getMassTextAttachmentLink,
} from "@markit/common.utils";
import StandardBorderedContainer from "../../../Containers/StandardBorderedContainer";
import { LightTheme } from "../../../../hooks/useTheme";
import { AttachmentsActionBarHoldItem } from "../../MassTexts/Message/AttachmentsActionBarHoldItem";
import DMAutomationAttachmentsPanel from "./DMAutomationAttachmentsPanel";
import DMAutomationContentButtonItem from "../Items/DMAutomationContentButtonItem";
import { getAccountState } from "../../../../redux/slices/accountSlice";
import { InsertPluginTextInput } from "../../MassTexts/Message/InsertPluginTextInput";
import { generate } from "shortid";
import ContentViolationAlert from "../../../DeliveryScore/ContentViolationAlert";
import ConfirmDiscardChangesModal from "../../../Containers/ConfirmPopups/ConfirmDiscardChangesModal";
import AlertContainer from "../../../Containers/AlertContainer";

type DMAutomationContentPanelProps = {
  dmAutomationContent: DMAutomationContent;
  isEditing: boolean;
  backOnPress: () => void;
  onSaveContent: (content: DMAutomationContent) => void;
};

const DMAutomationContentPanel = (props: DMAutomationContentPanelProps) => {
  const { dmAutomationContent, isEditing, backOnPress, onSaveContent } = props;
  const dispatch = useDispatch();
  const { accountData } = useSelector(getAccountState).account;
  const [savingContent, setSavingContent] = useState(false);
  const [localContent, setLocalContent] =
    useState<DMAutomationContent>(dmAutomationContent);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [showAttachmentModal, setShowAttachmentModal] =
    useState<boolean>(false);
  const [showContentViolationAlert, setShowContentViolationAlert] =
    useState(false);
  const [defaultAttachmentListView, setDefaultAttachmentListView] = useState(0);
  const [defaultAttachmentId, setDefaultAttachmentId] = useState<
    string | undefined
  >(undefined);
  const [showDiscardChangesModal, setShowDiscardChangesModal] = useState(false);
  const [alertText, setAlertText] = useState<{
    header: string;
    subHeader: string;
  }>({ header: "", subHeader: "" });

  const styles = {
    headerRow: {
      flexShrink: 0,
      height: 58,
      paddingInline: 14,
    },
    sectionContainer: {
      padding: 14,
      backgroundColor: Colors.WHITE1,
      borderRadius: 12,
      gap: 4,
    },
  };

  const hasInlineLink = useMemo(
    () => dmMessageContainsLink(localContent.message),
    [localContent.message]
  );

  const hasButtons = useMemo(
    () => localContent.buttons.length > 0,
    [localContent.buttons]
  );

  const madeChanges = useMemo(
    () =>
      localContent.message !== dmAutomationContent.message ||
      JSON.stringify(localContent.buttons) !==
        JSON.stringify(dmAutomationContent.buttons),
    [
      localContent.message,
      localContent.buttons,
      dmAutomationContent.message,
      dmAutomationContent.buttons,
    ]
  );

  const onAttachmentClick = useCallback(
    (key: MassTextAttachmentType | string) => {
      setDefaultAttachmentListView(defaultProductListView[key] ?? 0);
      if (!(key in defaultProductListView)) setDefaultAttachmentId(key);
      setShowAttachmentModal(true);
    },
    []
  );

  const finalizeAttachmentOnPress = useCallback(
    (attachmentItemId: string, buttonTitle: string, isInline: boolean) => {
      // Instagram DMs aren't carrier-filtered like SMS, so always link with the m4rkt.io
      // domain rather than the trustedSender-dependent one used for texts
      const url = getMassTextAttachmentLink(
        attachmentItemId,
        true,
        accountData.username
      );
      if (isInline) {
        const endsWithWhitespace = /\s$/.test(localContent.message);
        const messageWithUrl =
          localContent.message +
          (localContent.message.length === 0 || endsWithWhitespace ? "" : " ") +
          url;
        setLocalContent((prev) => ({ ...prev, message: messageWithUrl }));
      } else {
        const newButton: DMAutomationContentButton = {
          id: generate(),
          title: buttonTitle,
          url,
          type: DMAutomationContentButtonType.URL,
        };
        setLocalContent((prev) => ({
          ...prev,
          buttons: [...prev.buttons, newButton],
        }));
      }
      setShowAttachmentModal(false);
    },
    [accountData.username, localContent.message, setLocalContent]
  );

  const saveMessageOnPress = useCallback(async () => {
    // Prevent any censored terms
    if (detectedCensored(localContent.message)) {
      setShowContentViolationAlert(true);
      return;
    }

    // Instagram won't render a link in the message as clickable when a
    // button is also attached, so only one of the two is allowed at a time
    if (hasInlineLink && hasButtons) {
      setAlertText({
        header: "Use either an inline link or a button, not both",
        subHeader:
          "The inline link in your message won't be clickable when a button is also attached. Remove one to save.",
      });
      return;
    }

    if (!isEditing) {
      // For the creation flow
      onSaveContent(localContent);
    } else {
      // For the edit flow
      setSavingContent(true);
      const { contentId } = await API.dmAutomation.writeDmAutomationContent({
        content: localContent,
      });
      setSavingContent(false);
      onSaveContent({ ...localContent, id: contentId });
      showNotificationBanner(
        dispatch,
        "Message Updated!",
        NotificationType.AFFIRMATIVE
      );
    }
  }, [
    localContent,
    onSaveContent,
    dispatch,
    isEditing,
    hasInlineLink,
    hasButtons,
  ]);

  const renderContentButtons = useMemo(
    () => (
      <div className="ColumnNormal" style={{ gap: 10 }}>
        {localContent.buttons.map((button) => (
          <DMAutomationContentButtonItem
            key={button.id}
            contentButton={button}
            removeOnPress={() =>
              setLocalContent((prev) => ({
                ...prev,
                buttons: prev.buttons.filter((b) => b.id !== button.id),
              }))
            }
          />
        ))}
      </div>
    ),
    [localContent.buttons]
  );

  return (
    <>
      <div
        className="ColumnNormal PreventScroll"
        style={{ backgroundColor: Colors.WHITE }}
      >
        <div className="AlignedRowSpaced" style={styles.headerRow}>
          <div className="AlignedRow" style={{ gap: 10 }}>
            <BackButton
              onPress={() => {
                if (madeChanges) {
                  setShowDiscardChangesModal(true);
                } else {
                  backOnPress();
                }
              }}
              iconName="ion:chevron-back"
            />
            <span className="sectionTitle">Send a DM</span>
          </div>
          <div className="AlignedRow" style={{ gap: 10 }}>
            <RectangleButton
              buttonLabel={"Save"}
              onPress={saveMessageOnPress}
              loading={savingContent}
              disabled={!madeChanges}
              altColor={Colors.BLACK}
              altTextColor={Colors.WHITE}
              altBorderRadius={100}
              containerStyles={{ padding: "10px 14px" }}
            />
          </div>
        </div>
        <HorizontalDivider altMargin={0} />
        <div className="ColumnNormal">
          <div className="AlignedRow" style={{ padding: 14, gap: 10 }}>
            <span className="bodySubtext">To</span>
            <span className="bodyMedium">Started Flow</span>
          </div>
          <HorizontalDivider altMargin={0} />
          <div className="AlignedRow" style={{ padding: 14, gap: 10 }}>
            <span className="bodySubtext">When</span>
            <span className="bodyMedium">Sent Immediately</span>
          </div>
          <HorizontalDivider altMargin={0} />
        </div>
        <div
          className="ColumnNormal"
          style={{ paddingInline: 14, gap: 24, flex: 1 }}
        >
          <div style={{ flexGrow: 1, overflowY: "auto", paddingBottom: 14 }}>
            <InsertPluginTextInput
              trustedSender // always true since for DMs we can use the m4rkt.io domain
              message={localContent.message}
              setMessage={(message: string) =>
                setLocalContent((prev) => ({ ...prev, message: message }))
              }
              placeholder="Enter your message here..."
              pluginWords={new Set()}
              image={""}
              removeImage={() => {}}
              cursorPosition={cursorPosition}
              setCursorPosition={setCursorPosition}
              campaignId={""}
              event={undefined}
              fixedBottomText={renderContentButtons}
              maxLength={1000}
              disablePlugins
            />
          </div>
          <div
            className="ColumnNormal"
            style={{
              position: "sticky",
              bottom: 14,
              left: 14,
              right: 14,
              paddingTop: 24,
            }}
          >
            <StandardBorderedContainer
              containerStyles={{
                backgroundColor: Colors.WHITE1,
                padding: 14,
                borderRadius: 12,
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <div
                className="ColumnNormal"
                style={{ gap: 4, justifyContent: "flex-end" }}
              >
                <div className="AlignedRow" style={{ gap: 4 }}>
                  <span className="bodyMedium">
                    {localContent.message.length}/1000
                  </span>
                  {detectedCensored(localContent.message) ? (
                    <Icon
                      icon={"mdi:alert-circle-outline"}
                      height={15}
                      style={{ color: Colors.RED3, cursor: "pointer" }}
                      onClick={() => setShowContentViolationAlert(true)}
                    />
                  ) : null}
                </div>
              </div>
              <AttachmentsActionBarHoldItem
                onClick={onAttachmentClick}
                attachmentSourceType={AttachmentSourceType.DM_AUTOMATION}
                theme={LightTheme}
                invertedColor={true}
              />
            </StandardBorderedContainer>
          </div>
        </div>
      </div>
      {showAttachmentModal ? (
        <DMAutomationAttachmentsPanel
          closeOnPress={() => {
            setShowAttachmentModal(false);
            setDefaultAttachmentId(undefined);
            setDefaultAttachmentListView(0);
          }}
          defaultListView={defaultAttachmentListView}
          defaultAttachmentId={defaultAttachmentId}
          finalizeAttachment={finalizeAttachmentOnPress}
        />
      ) : null}
      <ContentViolationAlert
        message={localContent.message}
        isVisible={showContentViolationAlert}
        setIsVisible={setShowContentViolationAlert}
      />
      <ConfirmDiscardChangesModal
        isVisible={showDiscardChangesModal}
        setIsVisible={setShowDiscardChangesModal}
        deleteOnPress={backOnPress}
      />
      <AlertContainer
        headerComp={alertText.header}
        subHeaderComp={
          alertText.subHeader !== "" ? alertText.subHeader : undefined
        }
        closeModal={() => setAlertText({ header: "", subHeader: "" })}
        hideModal={alertText.header === ""}
      />
    </>
  );
};

export default DMAutomationContentPanel;
